import { ChevronUp, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PropertyKitItem, CONTROL_TYPE_LABELS } from '@/hooks/inventory';
import { usePropertyKitItems } from '@/hooks/inventory/usePropertyKitItems';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getThumbnailImageUrl } from '@/lib/cloudinary';

interface ItemsTableProps {
  items: PropertyKitItem[];
  onEdit: (itemId: string) => void;
  onReorder?: (itemId: string, swapItemId: string, newSortOrder: number, swapSortOrder: number) => Promise<void>;
  isReordering?: boolean;
}

export default function ItemsTable({ items, onEdit, onReorder, isReordering = false }: ItemsTableProps) {
  const { deleteItem, isDeleting } = usePropertyKitItems();

  const handleDelete = async (id: string) => {
    await deleteItem(id);
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0 || !onReorder) return;
    
    const current = items[index];
    const target = items[index - 1];
    
    await onReorder(
      current.id,
      target.id,
      target.sort_order,
      current.sort_order
    );
  };

  const handleMoveDown = async (index: number) => {
    if (index >= items.length - 1 || !onReorder) return;
    
    const current = items[index];
    const target = items[index + 1];
    
    await onReorder(
      current.id,
      target.id,
      target.sort_order,
      current.sort_order
    );
  };

  const getControlTypeBadge = (type: PropertyKitItem['control_type']) => {
    const colors = {
      fixed_asset: 'bg-blue-50 text-blue-700 border-blue-200',
      consumable: 'bg-amber-50 text-amber-700 border-amber-200',
      countable: 'bg-purple-50 text-purple-700 border-purple-200',
    };

    return (
      <Badge variant="outline" className={colors[type]}>
        {CONTROL_TYPE_LABELS[type]}
      </Badge>
    );
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Artículo</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Cantidad Ideal</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, index) => (
            <TableRow key={item.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  {item.image_url && (
                    <img
                      src={getThumbnailImageUrl(item.image_url)}
                      alt={item.name}
                      className="h-10 w-10 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <div className="font-medium">{item.name}</div>
                    {item.description && (
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {getControlTypeBadge(item.control_type)}
              </TableCell>
              <TableCell>
                {item.control_type === 'countable' && item.ideal_quantity != null
                  ? item.ideal_quantity
                  : '-'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {onReorder && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0 || isReordering}
                        className="h-8 w-8"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveDown(index)}
                        disabled={index >= items.length - 1 || isReordering}
                        className="h-8 w-8"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(item.id)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción no se puede deshacer. El artículo se eliminará del catálogo.
                          Si está en uso en algún kit, no podrá ser eliminado.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(item.id)}
                          disabled={isDeleting}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {isDeleting ? 'Eliminando...' : 'Eliminar'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
